import React, { useState, useEffect } from "react";
import {
  useGetSubModulesQuery,
  useCreateSubModuleMutation,
  useUpdateSubModuleMutation,
} from "../../../store/auth/authApi";
import { Pagination, EmptyTable, Loader } from "../../../components";

export default function SubModuleModal({
  isOpen,
  onClose,
  module = null,
  onSuccess,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSubModule, setEditingSubModule] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    url: "",
    module_id: module?.id || "",
  });

  const [createSubModule, { isLoading: isCreating }] =
    useCreateSubModuleMutation();
  const [updateSubModule, { isLoading: isUpdating }] =
    useUpdateSubModuleMutation();

  const {
    data: subModulesData,
    isLoading: subModulesLoading,
    error: subModulesError,
  } = useGetSubModulesQuery(
    {
      module_id: module?.id,
      page: currentPage,
      per_page: 40,
    },
    { skip: !isOpen || !module?.id }
  );

  const isEdit = !!editingSubModule;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery("");
  }, [module?.id]);

  useEffect(() => {
    if (editingSubModule) {
      setFormData({
        name: editingSubModule.name || "",
        url: editingSubModule.url || "",
        module_id: module?.id || "",
      });
    } else {
      setFormData({
        name: "",
        url: "",
        module_id: module?.id || "",
      });
    }
  }, [editingSubModule, module]);

  const filteredSubModules = Array.isArray(subModulesData?.data)
    ? subModulesData.data.filter((subModule) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
          subModule.name?.toLowerCase().includes(query) ||
          subModule.url?.toLowerCase().includes(query)
        );
      })
    : [];

  const [urlError, setUrlError] = useState("");
  const [apiError, setApiError] = useState("");

  const validateUrl = (url) => {
    const urlPattern = /^[a-zA-Z0-9_-]*$/;
    if (!urlPattern.test(url)) {
      return "URL may contain only letters, numbers, underscores, and hyphens";
    }
    if (url.includes("--") || url.includes("__")) {
      return "URL cannot contain consecutive separators";
    }
    if (/^[-_]|[-_]$/.test(url)) {
      return "URL cannot start or end with a hyphen or underscore";
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (apiError) {
      setApiError("");
    }

    let formattedValue = value;
    if (name === "url") {
      formattedValue = value
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "")
        .replace(/[-_]{2,}/g, "_");
      setUrlError(validateUrl(formattedValue));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setApiError("");
    setUrlError("");

    const validationError = validateUrl(formData.url);
    if (validationError) {
      setUrlError(validationError);
      return;
    }

    try {
      if (isEdit) {
        await updateSubModule({
          id: editingSubModule.id,
          ...formData,
        }).unwrap();
      } else {
        await createSubModule(formData).unwrap();
      }

      setIsCreateModalOpen(false);
      setEditingSubModule(null);
      onSuccess?.();
    } catch (error) {
      let errorMessage = "An error occurred while saving the sub-module";
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setApiError(errorMessage);
    }
  };

  const handleCreateSubModule = () => {
    setEditingSubModule(null);
    setApiError("");
    setUrlError("");
    setIsCreateModalOpen(true);
  };

  const handleEditSubModule = (subModule) => {
    setEditingSubModule(subModule);
    setApiError("");
    setUrlError("");
    setIsCreateModalOpen(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative z-[1001] shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Manage Sub-modules
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {module?.name} -{" "}
              {subModulesData?.total ?? filteredSubModules.length} sub-modules
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search sub-modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#8B313A] focus:border-[#8B313A] sm:text-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg
                  className="h-5 w-5 text-gray-400 hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={handleCreateSubModule}
            className="bg-[#8B313A] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Create Sub-module
          </button>
        </div>

        <div className="border border-gray-200 rounded-lg">
          {subModulesLoading ? (
            <div className="py-8">
              <Loader size="md" color="primary" text="Loading sub-modules..." />
            </div>
          ) : subModulesError ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">
                Error loading sub-modules:{" "}
                {subModulesError?.data?.message ||
                  subModulesError?.message ||
                  "Unknown error"}
              </p>
            </div>
          ) : filteredSubModules.length === 0 ? (
            <EmptyTable
              message={
                searchQuery
                  ? `No sub-modules found matching "${searchQuery}"`
                  : "No sub-modules available for this module"
              }
            />
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredSubModules.map((subModule) => (
                <div
                  key={subModule.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-gray-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-800">
                        {subModule.name}
                      </h3>
                      <p className="text-sm text-gray-500">{subModule.url}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEditSubModule(subModule)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                    title="Edit Sub-module"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {subModulesData && subModulesData.lastPage > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={subModulesData.lastPage || 1}
                hasNextPage={subModulesData.hasMorePages || false}
                hasPrevPage={currentPage > 1}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>

        {isCreateModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative z-[1101] shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  {isEdit ? "Edit Sub-module" : "Create New Sub-module"}
                </h3>
                <button
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingSubModule(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {apiError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-sm text-red-700">{apiError}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub-module Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
                    placeholder="Enter sub-module name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub-module URL
                  </label>
                  <input
                    type="text"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent"
                    placeholder="exit_requests"
                    required
                  />
                  {urlError && (
                    <p className="mt-1 text-sm text-red-600">{urlError}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setEditingSubModule(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || Boolean(urlError)}
                    className="px-6 py-2 bg-[#8B313A] text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <Loader
                          size="sm"
                          color="white"
                          variant="spinner"
                          className="mr-2"
                        />
                        {isEdit ? "Updating..." : "Creating..."}
                      </>
                    ) : isEdit ? (
                      "Update Sub-module"
                    ) : (
                      "Create Sub-module"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
